import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import InvestorVerifyFormModal from './components/InvestorVerifyFormModal';
import CheckCicleOverlapIcon from 'icons/CheckCicleOverlap.icon';
import ClockIcon from 'icons/Clock.icon';
import ResendIcon from 'icons/Resend.icon';
import CurrentProfileContext from '../../../../providers/CurrentProfile/CurrentProfile.context';
import { getFormattedDate } from 'dateUtils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import { Link } from 'react-router-dom';
import VerifiedBadge from 'components/VerifiedBadge';
import VerifiedIcon from 'icons/Verified.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import LogoutIcon from 'icons/Logout.icon';
import { INVESTOR_VERIFICATION_ROUTE } from 'data/global';

import './InvestorVerify.styles.scss';
import './InvestorVerify.styles.responsive.scss';

type InvestorVerifyProps = {
  className?: string;
};

const InvestorVerify: React.FC<InvestorVerifyProps> = (props) => {
  const { className } = props;
  const [isResending, setIsResending] = useState(false);
  const { currentProfile } = useContext(CurrentProfileContext);
  const { accountant } = currentProfile || {};
  const modalRef = useRef(null);
  const { t } = useTranslation();
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const handleResend = useCallback(async () => {
    try {
      setIsResending(true);
      await api.accountants.resendForm();
      popSuccess(t('resendInvestorFormSuccess'));
    } catch (error) {
      popServerError(error);
    } finally {
      setIsResending(false);
    }
  }, [popServerError, popSuccess, t]);

  const Badge = useMemo(() => {
    if (!accountant || accountant?.formStatus === 'not_sent') return null;
    const { formStatus } = accountant;

    if (formStatus === 'pending')
      return <div className="badge">{t(formStatus)}</div>;

    return <VerifiedBadge text={t('investor')} />;
  }, [accountant, t]);

  const RenderContent = useMemo(() => {
    const { formStatus, accountantDetails, completedDate, sentDate } =
      accountant || {};

    if (!formStatus || formStatus === 'not_sent') {
      return (
        <div className="not-sent-actions">
          <Button
            variant="outline"
            icon={LogoutIcon}
            iconPosition="left"
            onClick={() => modalRef.current.open()}
          >
            {t('sendToYourAccountant')}
          </Button>
          <Link to={INVESTOR_VERIFICATION_ROUTE}>
            <LinkToIcon /> {t('previewForm')}
          </Link>
          <InvestorVerifyFormModal ref={modalRef} />
        </div>
      );
    }

    if (formStatus === 'pending') {
      return (
        <div className={`investor-verify__content ${formStatus}`}>
          <p>
            <ClockIcon />
            {t('sentTo', {
              name: `${accountantDetails?.firstName} ${accountantDetails?.lastName}`,
              date: getFormattedDate(sentDate),
            })}
          </p>
          <p>{t('awaitingCompletion')}</p>
          <Button
            icon={ResendIcon}
            variant="link"
            onClick={handleResend}
            disabled={isResending}
          >
            {t('resendAgain')}
          </Button>
        </div>
      );
    }

    return (
      <div className={`investor-verify__content ${formStatus}`}>
        <p>
          <CheckCicleOverlapIcon />
          {t('sentTo', {
            name: `${accountantDetails?.firstName} ${accountantDetails?.lastName}`,
            date: getFormattedDate(sentDate),
          })}
        </p>
        <p>
          <CheckCicleOverlapIcon />
          {t('formCompletedOn', {
            date: getFormattedDate(completedDate),
          })}
        </p>
      </div>
    );
  }, [accountant, handleResend, isResending, t]);

  const RenderPromoTile = useMemo(
    () => (
      <div className="promo-tile">
        <VerifiedIcon />

        <p>{t('promoTileDescription')}</p>
      </div>
    ),
    [t],
  );

  const classes = classNames('investor-verify', className);

  return (
    <div className={classes}>
      <div className="investor-verify__left">
        {Badge}
        <p className="profile-section-title">{t('investorVerifyTitle')}</p>
        {accountant?.formStatus !== 'completed' && (
          <p className="description">{t('investorVerifyDescription')}</p>
        )}
        {RenderContent}
      </div>
      {RenderPromoTile}
    </div>
  );
};

export default InvestorVerify;
